/**
 * DocumentSidebar Component
 * Collapsible sidebar for uploading documents during survey
 * Documents provide context to the AI for better follow-up questions
 */

import React, { useState, useRef, useCallback } from 'react';
import { uploadDocument, listDocuments, deleteDocument } from '../services/api';

/**
 * Document upload sidebar component
 * @param {Object} props
 * @param {string} props.token - Survey session token
 */
function DocumentSidebar({ token }) {
    const [isOpen, setIsOpen] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    // Allowed file types
    const ALLOWED_TYPES = [
        'text/plain',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/markdown',
        'image/png',
        'image/jpeg'
    ];
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB

    /**
     * Load documents when sidebar opens
     */
    const loadDocuments = useCallback(async () => {
        if (!token) return;
        try {
            const docs = await listDocuments(token);
            setDocuments(docs);
        } catch (err) {
            console.error('Failed to load documents:', err);
        }
    }, [token]);

    /**
     * Toggle sidebar open/closed
     */
    const toggleSidebar = () => {
        const newState = !isOpen;
        setIsOpen(newState);
        if (newState) {
            loadDocuments();
        }
    };

    /**
     * Handle file selection
     */
    const handleFileSelect = async (files) => {
        if (!files || files.length === 0) return;

        const file = files[0];

        // Validate file type
        if (!ALLOWED_TYPES.includes(file.type)) {
            setError('File type not supported. Please upload PDF, Word, text, or image files.');
            return;
        }

        // Validate file size
        if (file.size > MAX_SIZE) {
            setError('File is too large. Maximum size is 10MB.');
            return;
        }

        setIsUploading(true);
        setError(null);

        try {
            const result = await uploadDocument(token, file);
            setDocuments(prev => [result, ...prev]);
        } catch (err) {
            setError(err.message || 'Failed to upload document');
        } finally {
            setIsUploading(false);
        }
    };

    /**
     * Handle file input change
     */
    const handleInputChange = (e) => {
        handleFileSelect(e.target.files);
        e.target.value = ''; // Reset input
    };

    /**
     * Handle drag events
     */
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    /**
     * Handle drop event
     */
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        handleFileSelect(e.dataTransfer.files);
    };

    /**
     * Handle document deletion
     */
    const handleDelete = async (docId) => {
        try {
            await deleteDocument(token, docId);
            setDocuments(prev => prev.filter(d => d.id !== docId));
        } catch (err) {
            setError(err.message || 'Failed to delete document');
        }
    };

    /**
     * Format file size for display
     */
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    /**
     * Get icon for file type
     */
    const getFileIcon = (type) => {
        if (type.startsWith('image/')) return '🖼️';
        if (type.includes('pdf')) return '📄';
        if (type.includes('word') || type.includes('document')) return '📝';
        if (type.includes('text')) return '📃';
        return '📁';
    };

    return (
        <>
            {/* Toggle Button */}
            <button 
                className={`document-toggle ${isOpen ? 'open' : ''}`}
                onClick={toggleSidebar}
                title="Upload supporting documents"
            >
                <span className="toggle-icon">📎</span>
                <span className="toggle-label">Documents</span>
                {documents.length > 0 && (
                    <span className="document-count">{documents.length}</span>
                )}
            </button>

            {/* Sidebar Panel */}
            <div className={`document-sidebar ${isOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <h3>📎 Supporting Documents</h3>
                    <button className="close-btn" onClick={toggleSidebar}>×</button>
                </div>

                <div className="sidebar-content">
                    {/* Info Text */}
                    <p className="sidebar-info">
                        Upload documents to help the AI understand your work better. 
                        This could include meeting notes, project docs, or job descriptions.
                    </p>

                    {/* Upload Area */}
                    <div 
                        className={`upload-area ${dragActive ? 'drag-active' : ''} ${isUploading ? 'uploading' : ''}`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleInputChange}
                            accept=".txt,.pdf,.doc,.docx,.md,.png,.jpg,.jpeg"
                            style={{ display: 'none' }}
                        />
                        {isUploading ? (
                            <>
                                <span className="upload-spinner"></span>
                                <span>Uploading...</span>
                            </>
                        ) : (
                            <>
                                <span className="upload-icon">📤</span>
                                <span>Drop files here or click to upload</span>
                                <span className="upload-hint">PDF, Word, text, images (max 10MB)</span>
                            </>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="upload-error">
                            <span>⚠️ {error}</span>
                            <button onClick={() => setError(null)}>×</button>
                        </div>
                    )}

                    {/* Document List */}
                    <div className="document-list">
                        {documents.length === 0 ? (
                            <p className="no-documents">No documents uploaded yet</p>
                        ) : (
                            documents.map(doc => (
                                <div key={doc.id} className="document-item">
                                    <span className="doc-icon">{getFileIcon(doc.file_type)}</span>
                                    <div className="doc-info">
                                        <span className="doc-name" title={doc.filename}>
                                            {doc.filename.length > 25 
                                                ? doc.filename.substring(0, 22) + '...' 
                                                : doc.filename}
                                        </span>
                                        <span className="doc-size">{formatFileSize(doc.file_size)}</span>
                                    </div>
                                    {doc.has_extracted_text && (
                                        <span className="doc-extracted" title="Text extracted for AI">✓</span>
                                    )}
                                    <button 
                                        className="delete-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(doc.id);
                                        }}
                                        title="Delete document"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Backdrop */}
            {isOpen && <div className="sidebar-backdrop" onClick={toggleSidebar} />}
        </>
    );
}

export default DocumentSidebar;
